import React, { useEffect, useState, lazy } from 'react'
import { useLocation } from 'react-router-dom'
import { Redirect, Route, Switch } from 'react-router-dom'
import { ContentHeader } from '../components/Header/ContentHeader'
import { IRoute } from '../types/route'
import { IUserDetail } from '../types/user'
import { checkRole } from '../utils/checkRole'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import AccountingHeaderContainer from './accounting/containers/AccountingHeaderContainer'
import ProjectAccountingHeaderContainer from './project/containers/accounting/AccountingHeaderContainer'
import InvoiceListHeaderContainer from './project/containers/accounting/InvoiceListHeaderContainer'
import PaymentListHeaderContainer from './project/containers/accounting/PaymentListHeaderContainer'
import ProjectActivitiesHeader from './project/containers/activities/ProjectActivitiesHeader'
import ProjectBudgetHeader from './project/containers/budget/BudgetListHeaderContainer'
import ProjectOfferHeader from './project/containers/budget/service-provider/ServiceProviderProjectOfferHeaderContainer'
import ProjectDashboardHeader from './project/containers/dashboard/ProjectDashboardHeader'
import ProjectInsightsHeader from './project/containers/insights/ProjectInsightsHeader'
import ProjectListHeaderContainer from './project/containers/list/ProjectListHeaderContainer'
import ProjectMilestonesHeader from './project/containers/milestones/MilestonesHeaderContainer'
import ProjectSettingsHeader from './project/containers/project-settings/ProjectSettingsHeaderContainer'
import ReportsHeaderContainer from './reports/containers/ReportsHeaderContainer'
import InvolvedTasksHeaderContainer from './time-tracking/containers/TimeTrackingInvolvedTasksHeaderContainer'
import BudgetHeaderContainer from './budget/containers/BudgetHeaderContainer'
import BudgetDetailHeaderContainer from './budget/containers/BudgetDetailHeaderContainer'
import BudgetSettingsHeaderContainer from './budget/containers/BudgetSettingsHeaderContainer'

interface Props {
  user: IUserDetail | null
  loggedIn: boolean
  haveRight: boolean
  checkLoader: boolean
  projectAdminRight: string[]
}

// Header Components
const DashboardHeader = () => <ContentHeader title='Dashboard' color='#232C58' />

const globalDashboardRoutes: IRoute[] = [
  {
    exact: true,
    path: '/dashboard',
    component: DashboardHeader,
    rights: [27],
  },
]

const userDashboardRoutes: IRoute[] = [
  {
    exact: true,
    path: '/user-dashboard',
    component: DashboardHeader,
    rights: [],
  },
]

const projectRoutes: IRoute[] = [
  { exact: true, path: '/projects', component: ProjectListHeaderContainer, rights: [6] },
  { exact: true, path: '/projects/:id([0-9]+)/activities', component: ProjectActivitiesHeader, rights: [6] },
  { exact: true, path: '/projects/:id([0-9]+)/dashboard', component: ProjectDashboardHeader, rights: [6] },
  { exact: false, path: '/projects/:id([0-9]+)/insights', component: ProjectInsightsHeader, rights: [21] },
  { exact: true, path: '/projects/:id([0-9]+)/budget', component: ProjectBudgetHeader, rights: [16] },
  { exact: true, path: '/projects/:id([0-9]+)/milestones', component: ProjectMilestonesHeader, rights: [28] },
  { exact: true, path: '/projects/:id([0-9]+)/expenditures', component: ProjectBudgetHeader, rights: [16] },
  { exact: true, path: '/projects/:id([0-9]+)/accounting', component: ProjectAccountingHeaderContainer, rights: [19] },
  {
    exact: true,
    path: '/projects/:id([0-9]+)/accounting/payment-list',
    component: PaymentListHeaderContainer,
    rights: [19],
  },
  {
    exact: true,
    path: '/projects/:id([0-9]+)/accounting/invoice-list',
    component: InvoiceListHeaderContainer,
    rights: [19],
  },
  { exact: false, path: '/projects/:id([0-9]+)/settings', component: ProjectSettingsHeader, rights: [14] },
  /** Budget Routes **/
  { exact: true, path: '/projects/:id([0-9]+)/budget/project-offer', component: ProjectOfferHeader, rights: [] },
]

// Admin Header Components
const AdminEmptyHeader = () => <div></div>
const AdminGeneralHeader = () => <ContentHeader title='General' color='#232C58' marginTop='60px' />
const AdminUserHeader = () => <ContentHeader title='User' color='#232C58' marginTop='60px' />
const AdminRatesHeader = () => <ContentHeader title='Rates' color='#232C58' marginTop='60px' />
const TimeTrackingHeader = () => <ContentHeader title='Time Tracking' color='#232C58' />

const adminRoutes: IRoute[] = [
  { exact: true, path: '/admin', component: AdminEmptyHeader, rights: [1, 2, 3, 4, 5] },
  { exact: true, path: '/admin/general', component: AdminGeneralHeader, rights: [1] },
  { exact: true, path: '/admin/user', component: AdminUserHeader, rights: [2, 3, 4] },
  { exact: true, path: '/admin/rates', component: AdminRatesHeader, rights: [5] },
  { exact: true, path: '/profile', component: AdminEmptyHeader, rights: [] },
]

const settingsRoutes: IRoute[] = [...adminRoutes]

const timeTrackingRoutes: IRoute[] = [
  { exact: true, path: '/time-tracking', component: TimeTrackingHeader, rights: [22] },
  { exact: true, path: '/time-tracking/involved-tasks', component: InvolvedTasksHeaderContainer, rights: [22] },
]

const accountingRoutes: IRoute[] = [
  { exact: true, path: '/accounting', component: AccountingHeaderContainer, rights: [25, 26] },
  { exact: true, path: '/accounting/:id([0-9]+)', component: AccountingHeaderContainer, rights: [25, 26] },
]

const reportsRoutes: IRoute[] = [
  { exact: true, path: '/reports', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/tasks-by-project', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/project-list', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/fte-by-project', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/expenditures-phasing', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/department-utilization', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/project-hours-phasing', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/expected-payments', component: ReportsHeaderContainer, rights: [24] },
  { exact: true, path: '/reports/expected-receipts', component: ReportsHeaderContainer, rights: [24] },
]


// Budget Module Routes - Header component
const BudgetTransferHeader = () => <ContentHeader title='Budget Transfer' color='#232C58' />

const budgetRoutes: IRoute[] = [
  { exact: true, path: '/budget', component: BudgetHeaderContainer, rights: [29] }, // Budget header for budget page
  { exact: true, path: '/budget/transfer', component: BudgetTransferHeader, rights: [29] }, // Budget transfer header
  { exact: true, path: '/budget/settings', component: BudgetSettingsHeaderContainer, rights: [29] }, // Budget settings header
  // Budget settings tab-specific header routes
  { exact: true, path: '/budget/settings/internal', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/external', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/units', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/templates', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/clients', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/supplier', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/settings/categories', component: BudgetSettingsHeaderContainer, rights: [29] },
  { exact: true, path: '/budget/new', component: BudgetHeaderContainer, rights: [29] }, // Budget creation header
  { exact: true, path: '/budget/:id/edit', component: BudgetHeaderContainer, rights: [29] }, // Budget editing header
  { exact: true, path: '/budget/:id([0-9]+)/detail', component: BudgetDetailHeaderContainer, rights: [29] }, // Budget detail header
]

const routes: IRoute[] = [
  ...budgetRoutes,  // Add budget routes to prevent redirect
  ...globalDashboardRoutes,
  ...userDashboardRoutes,
  ...projectRoutes,
  ...settingsRoutes,
  ...timeTrackingRoutes,
  ...accountingRoutes,
  ...reportsRoutes,
  /** Default */
  { exact: false, path: '/dashboard', component: null, rights: [] },
]

const Forbidden = (): JSX.Element => {
  return (
    <ContentHeader
      title={'Forbidden.'}
      subTitleFont='1.3rem'
      subtitle={'Sorry, it appears you do not have the rights to access this content.'}
      leftItems={
        <div style={{ fontStyle: 'italic' }}>Please contact your Administrator to resolve this Problem...</div>
      }
    />
  )
}

const EmptyComponent = () => <></>
const ConditionalForbidden = ({ user, checkLoader, checkRole, rights }: any) => {
  if (user && (user.id === '' || checkRole(user, rights)) && checkLoader) {
    return <></>
  }
  return <Forbidden />
}

const ProtectedHeaderRoutes = (props: Props): JSX.Element => {
  const { user, loggedIn, haveRight, checkLoader, projectAdminRight } = props
  const [admin, setAdmin] = useState(false)
  const [projectRight, setProjectRight] = useState(true)
  const [projectSubmenuRight, setProjectSubmenuRight] = useState(true)
  const _location = useLocation()
  const { featureFlags } = useFeatureFlags()

  useEffect(() => {
    const locArr = _location.pathname.split('/').filter((i) => i !== '')
    const projectView = locArr.length > 1 && locArr.includes('projects')
    const adminRole = user?.employee?.roles.filter((r) => +r.id === 1000).length !== 0
    if (projectView && adminRole) {
      setAdmin(true)
    }
  }, [_location, user])

  useEffect(() => {
    const locArr = _location.pathname.split('/').filter((i) => i !== '')
    const projectView = locArr.length > 1 && locArr.includes('projects')
    setProjectRight(projectView ? haveRight : true)
    setProjectSubmenuRight(projectView ? projectAdminRight.some(i => locArr.includes(i)) : true)
  }, [_location, haveRight, projectAdminRight])

  const isRouteRestricted = (routePath: string): boolean => {
    if (!featureFlags || !featureFlags.enable_restricted_access_mode) return false

    if (routePath.includes('/budget')) return false
    if (routePath.includes('/admin')) return false
    if (routePath.includes('/user-dashboard')) return false

    if (routePath.includes('/dashboard')) {
      return featureFlags.restricted_features.dashboard || false
    }
    if (routePath.includes('/projects')) {
      return featureFlags.restricted_features.projects || false
    }
    if (routePath.includes('/time-tracking')) {
      return featureFlags.restricted_features.time_tracking || false
    }
    if (routePath.includes('/accounting')) {
      return featureFlags.restricted_features.accounting || false
    }
    if (routePath.includes('/reports')) {
      return featureFlags.restricted_features.reports || false
    }
    return false
  }

  // Filter out admin, profile, and budget routes from the main routes - they're rendered separately
  const filteredRoutes = routes.filter(route =>
    !route.path.startsWith('/admin') &&
    route.path !== '/profile' &&
    !route.path.startsWith('/budget')
  )

  return (
    <Switch>
      {/* Admin and Profile Routes - rendered separately to bypass project permission checks */}
      {settingsRoutes.map((route, idx) => (
        <Route
          key={`settings-${idx}`}
          exact={route.exact}
          path={route.path}
          component={route.component}
        />
      ))}

      {/* Budget Routes - rendered separately */}
      {budgetRoutes.map((route, idx) => (
        <Route
          key={`budget-${idx}`}
          exact={route.exact}
          path={route.path}
          component={route.component}
        />
      ))}

      {/* Other Routes with permission checks */}
      {filteredRoutes.map((route, idx) => {
        // Skip routes without components
        if (!route.component) {
          return null
        }
        if (isRouteRestricted(route.path)) {
          return <Route key={idx} exact={route.exact} path={route.path} component={EmptyComponent} />
        }
        const hasPermission = (user && user.id !== '' || loggedIn) && projectRight && projectSubmenuRight && ((user && checkRole(user, route.rights)) || admin)

        if (hasPermission) {
          return <Route key={idx} exact={route.exact} path={route.path} component={route.component} />
        }
        return <Route
          key={idx}
          exact={false}
          path={route.path}
          component={Forbidden}
        />
      })}
    </Switch>
  )
}

export default ProtectedHeaderRoutes
